import React, {useState, useEffect, useRef, useMemo} from 'react';
import {PatientHeader, AISummary, PatientFiles, ClinicalNoteEditableForm, Attachments, ActionButtons, ChatPanel, PreviousCaseDetails} from '@/components';
import ReferralSection from '@/components/consultation-screen/specialist-consultation/summary-screen/ReferralSection';
import {ConsultationSummary as ConsultationSummaryType, ConsultationReferral, UpdateConsultationSummaryRequestPayload} from '@/interface';
import {useSelector, useDispatch} from 'react-redux';
import {specialistDashboardConsultation, specialistDashboardSuccessStatus, specialistDashboardFailureStatus} from '@/services/redux/selectors';
import {formatConsultationDate, positionChatPanel} from '@/utils/common_functions';
import {
  getConsultationSummaryRequest,
  resetSpecialistDashboardMessage,
  updateConsultationSummaryRequest,
  uploadSpecialistConsultationAdditionalFilesRequest,
  getSpecializationRequest,
} from '@/services/redux/slices';
import {useNavigate, useParams} from 'react-router-dom';
import {useFormik} from 'formik';
import {useToast, useConfimationModal} from '@/hooks';
import {Routes} from '@/navigation/Routes';
import {ErrorCodes, getErrorMessage, getSuccessMessage, SuccessCodes} from '@/utils/getMessages';
import {ConsultationGroup, ConsultationStatus, ConsultationType, ReferralType} from '@/constants/enums';
import {isEmpty} from 'ramda';

type ConsultationSummaryInitalValues = {
  summary: ConsultationSummaryType['summary'];
  referral: ConsultationReferral | null;
};

const defaultInitialValues: ConsultationSummaryInitalValues = {
  summary: {
    chief_complaint: '',
    history_summary: '',
    diagnoses: [],
    medication: [],
    plan: '',
    recommended_next_steps: '',
  },
  referral: null,
};

export const ConsultationSummary: React.FC = () => {
  //==============================================//
  // hooks
  //==============================================//
  const {id} = useParams();
  const dispatch = useDispatch();
  const {showToast} = useToast();
  const navigate = useNavigate();
  const {showModal} = useConfimationModal();
  const chatPanelRef = useRef<HTMLDivElement>(null);

  //==============================================//
  // selectors
  //==============================================//
  const consultationSummary = useSelector(specialistDashboardConsultation);
  const successStatus = useSelector(specialistDashboardSuccessStatus) as SuccessCodes;
  const failureStatus = useSelector(specialistDashboardFailureStatus) as ErrorCodes;

  const {values, handleSubmit, setFieldValue} = useFormik<ConsultationSummaryInitalValues>({
    initialValues: defaultInitialValues,
    enableReinitialize: true,
    onSubmit: onSubmitSecondOpinion,
  });

  //==============================================//
  // state management
  //==============================================//
  const [expandedSections, setExpandedSections] = useState({
    patientFiles: false,
    referral: false,
  });
  const isReadonly = useMemo(
    () =>
      (consultationSummary?.status &&
        ![ConsultationStatus.AwaitingDoctorReview, ConsultationStatus.UnderReviewByDoctor, ConsultationStatus.Overdue].includes(consultationSummary?.status)) ||
      false,
    [consultationSummary?.status],
  );
  const isSumbitting = useRef<boolean>(false);
  // whather user has clicked the save as draft button or not
  const isManuallySubmitted = useRef<boolean>(false);
  // whether user is saving recommended next steps specifically
  const isSavingRecommendedNextSteps = useRef<boolean>(false);
  const [isChatPanelOpen, setIsChatPanelOpen] = useState<boolean>(false);

  //==============================================//
  // event handlers and functions
  //==============================================//
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  function onSubmitSecondOpinion(values: ConsultationSummaryInitalValues) {
    // Ensure all medications have IDs
    const processedSummary = {
      ...values.summary,
      medication: values.summary.medication.map((med, index) => ({
        ...med,
        id: med.id || Date.now() + index, // Assign ID if missing
      })),
    };

    const payload: UpdateConsultationSummaryRequestPayload = {
      id: Number(id),
      summary: processedSummary,
      submit: isSumbitting.current,
    };
    if (values.referral) {
      payload.referral = values.referral;
    }
    dispatch(updateConsultationSummaryRequest(payload));
  }

  const handleSaveAsDraft = () => {
    if (
      consultationSummary &&
      [ConsultationStatus.AwaitingDoctorReview, ConsultationStatus.UnderReviewByDoctor, ConsultationStatus.Overdue].includes(consultationSummary.status)
    ) {
      isSumbitting.current = false;
      handleSubmit();
    }
  };

  function handleSubmitSecondOpinion() {
    showModal({
      onConfirmHandler: () => {
        isSumbitting.current = true;
        handleSubmit();
      },
      cancelButtonText: 'No, Go Back',
      confirmButtonText: 'Yes, Submit',
      header: 'Are you sure you’ve reviewed all clinical points and patient\ncase details before submitting your second opinion?',
      warning: 'Note: Once submitted, you won’t be able to edit it.',
      primaryCta: 'confirm',
      showCloseButton: true,
      cancelButtonClassName: 'max-w-[200px]',
      confirmButtonClassName: 'max-w-[200px]',
    });
  }

  function handleGotoPastConsultations() {
    navigate(Routes.CONSULTATION, {
      state: {tab: ConsultationGroup.Past},
    });
  }

  function handleUploadFiles(files: File[]) {
    if (!consultationSummary) return;
    const payload = new FormData();
    for (const file of files) {
      payload.append('files', file);
    }
    payload.append('id', consultationSummary.id.toString());
    dispatch(uploadSpecialistConsultationAdditionalFilesRequest(payload));
  }

  function handleChatClick(event?: React.MouseEvent<HTMLButtonElement>) {
    setIsChatPanelOpen(true);

    const buttonRect = event?.currentTarget?.getBoundingClientRect();
    // only set the top position of the chat panel

    // keep the chat panel at the bottom of the page
    positionChatPanel({buttonRect, chatPanelRef, margin_x: 100});
  }

  //==============================================//
  // side effects
  //==============================================//
  // autosave (as draft when going to different screen)
  useEffect(() => {
    // return () => {
    //   setTimeout(handleSaveAsDraft, 500);
    // };
  }, []);
  // ======= side effect to handle the success status
  useEffect(() => {
    if (successStatus) {
      if (successStatus === 'S-10042') {
        dispatch(getConsultationSummaryRequest({id: Number(id)}));
        if (isManuallySubmitted.current) {
          showToast(getSuccessMessage(successStatus), 'success');
        }
        // Show toast for recommended next steps save
        if (isSavingRecommendedNextSteps.current) {
          showToast(getSuccessMessage('S-10130'), 'success');
          isSavingRecommendedNextSteps.current = false;
        }

        // reset the ref value for is manually submitted
        isManuallySubmitted.current = false;
      }
      // When referral is confirmed from the summary screen
      if (successStatus === 'S-10119') {
        dispatch(getConsultationSummaryRequest({id: Number(id)}));
        showToast(getSuccessMessage(successStatus), 'success');
      }
      // when consultation summary submitted successfully, goto past consultations
      if (successStatus === 'S-10047') {
        handleGotoPastConsultations();
      }
    }

    // reset the success status
    if (failureStatus) {
      showToast(getErrorMessage(failureStatus), 'failure');
    }

    return () => {
      dispatch(resetSpecialistDashboardMessage());
    };
  }, [successStatus, failureStatus]);

  // ======= side effect to set the initial values
  useEffect(() => {
    if (consultationSummary?.summary) {
      // Ensure all medications have IDs when loading from API
      const processedSummary = {
        ...consultationSummary.summary,
        medication: consultationSummary.summary.medication.map((med, index) => ({
          ...med,
          id: med.id || Date.now() + index, // Assign ID if missing
        })),
      };
      setFieldValue('summary', processedSummary);
    }
  }, [consultationSummary?.summary]);

  // ======= side effect to set the referral
  useEffect(() => {
    if (consultationSummary?.referral && !isEmpty(consultationSummary?.referral)) {
      const formattedRefferal = JSON.parse(JSON.stringify(consultationSummary.referral)); // deep clone this
      if (formattedRefferal.type === ReferralType.External && formattedRefferal.data?.phone?.length < 4) {
        formattedRefferal.data.phone = '';
      }
      setFieldValue('referral', formattedRefferal);
    } else {
      setFieldValue('referral', null);
    }
  }, [consultationSummary?.referral]);

  // ======= side effect to get the consultation summary
  useEffect(() => {
    dispatch(getConsultationSummaryRequest({id: Number(id)}));

    // also fetch latest specialization list on page load such that always latest specialization list is available for the referral section
    dispatch(getSpecializationRequest());
  }, []);

  return (
    <div className="bg-dashboardBackground p-3 sm:p-4 md:p-6 gap-3 sm:gap-4 md:gap-6 flex flex-col">
      <PatientHeader
        patientName={consultationSummary?.patient_name || ''}
        caseId={consultationSummary?.id.toString() || ''}
        category={consultationSummary?.category.label || ''}
        receivedOn={formatConsultationDate(consultationSummary?.created_at || '')}
        reviewAt={formatConsultationDate(consultationSummary?.review_at || '')}
      />

      {/* previous case */}
      {consultationSummary?.previous_consultation && consultationSummary.type === ConsultationType['Referral Second Opinion'] && (
        <PreviousCaseDetails previousConsultation={consultationSummary.previous_consultation} />
      )}

      {/* ai summary */}
      <AISummary
        summary={consultationSummary?.ai_summary || ''}
        prev_summary={consultationSummary?.previous_consultation?.ai_summary}
        prev_case_close_at={consultationSummary?.previous_consultation?.close_at}
      />

      {/* patient files */}
      <PatientFiles
        isExpanded={expandedSections.patientFiles}
        onToggle={() => toggleSection('patientFiles')}
        files={consultationSummary?.files || []}
        patientQuery={consultationSummary?.patient_query || ''}
      />

      {/* clinical note */}
      <ClinicalNoteEditableForm
        isReadonly={isReadonly}
        initialValues={values.summary}
        onSubmit={values => {
          setFieldValue('summary', values);
          handleSaveAsDraft();
        }}
        onRecommendedNextStepsSave={() => {
          isSavingRecommendedNextSteps.current = true;
        }}
      />

      {/* attachments */}
      <Attachments
        case_id={consultationSummary?.id}
        isReadonly={isReadonly}
        files={consultationSummary?.additional_files || []}
        onUploadFile={(files: File[]) => handleUploadFiles(files)}
      />

      {/* referral */}
      {consultationSummary?.consultation_type === ConsultationType['Online Second Opinion'] && (
        <ReferralSection
          initialValues={values.referral ?? null}
          onSubmit={values => {
            setFieldValue('referral', values);
            handleSaveAsDraft();
          }}
          isReadonly={isReadonly}
          case_id={consultationSummary?.id}
          hospital_specializations={consultationSummary?.hospital_specializations}
        />
      )}
      {/* action buttons */}
      {!isReadonly && (
        <ActionButtons
          chatId={consultationSummary?.id || 0}
          onSaveAsDraft={() => {
            isManuallySubmitted.current = true;
            handleSaveAsDraft();
          }}
          onSubmitSecondOpinion={handleSubmitSecondOpinion}
          onChatClick={handleChatClick}
          disableSubmitSecondOpinion={!values.summary.recommended_next_steps}
          meet_link={consultationSummary?.meet_link}
        />
      )}

      {consultationSummary && (
        <ChatPanel
          chatPanelRef={chatPanelRef}
          open={isChatPanelOpen}
          reciever_profile_photo={consultationSummary?.patient_profile || ''}
          case_status={consultationSummary?.status}
          reciever_name={consultationSummary?.patient_name}
          case_id={consultationSummary?.id}
          onClose={() => setIsChatPanelOpen(false)}
        />
      )}
    </div>
  );
};
